import { Fragment, useState, useMemo } from 'react';
import { Dialog, Transition, Menu } from '@headlessui/react';
import { TEST_IDS, testId } from '@lib/utils/testIds';
import {
  Bars3Icon,
  XMarkIcon,
  Squares2X2Icon,
  DocumentTextIcon,
  UserGroupIcon,
  ShoppingBagIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  UsersIcon,
  BuildingStorefrontIcon,
  ArchiveBoxIcon,
  ChartBarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentPlusIcon,
  CreditCardIcon,
  QuestionMarkCircleIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { cn } from '@lib/utils/cn';
import { normalizeRole } from '@lib/utils/roleNavigation';
import { useAuth } from '@features/auth/hooks/useAuth';

import { Logo } from '@components/common/Logo';
import { TenantSwitcher } from '@features/companies/components/TenantSwitcher';
import { trackingHelpers } from '@/services/mixpanel';

// Role constants - normalized (uppercase, no spaces)
const ROLES = {
  SUPERADMIN: 'SUPERADMIN',
  ADMINISTRATOR: 'ADMINISTRATOR',
  COMPANY_ADMIN: 'COMPANYADMIN',
  VENDEDOR: 'VENDEDOR',
  SELLER: 'SELLER',
  SALESPERSON: 'SALESPERSON',
  CUSTOMER_B2B: 'CUSTOMERB2B',
} as const;

// Navigation items with role-based access
// SUPERADMIN: Full access to everything + Company Selector
// ADMINISTRATOR: Full access (including Planes)
// COMPANY ADMIN: Everything EXCEPT Planes y Suscripción
// VENDEDOR / CUSTOMER B2B: Facturas Activas, Nueva Factura, Clientes, Productos, Soporte
const allNavigation: Array<{ name: string; href: string; icon: typeof Squares2X2Icon; roles: string[] | 'all' }> = [
  { name: 'Dashboard', href: '/dashboard', icon: Squares2X2Icon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR, ROLES.COMPANY_ADMIN] },
  { name: 'Facturas Activas', href: '/home/main', icon: DocumentTextIcon, roles: 'all' },
  { name: 'Archivo Histórico', href: '/home/minor', icon: ArchiveBoxIcon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR, ROLES.COMPANY_ADMIN] },
  { name: 'Nueva Factura', href: '/invoices/new', icon: DocumentPlusIcon, roles: 'all' },
  { name: 'Clientes', href: '/customers', icon: UserGroupIcon, roles: 'all' },
  { name: 'Productos', href: '/products', icon: ShoppingBagIcon, roles: 'all' },
  { name: 'Reportes', href: '/report/reports', icon: ChartBarIcon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR, ROLES.COMPANY_ADMIN] },
];

const allCompanyNav = [
  { name: 'Mi Empresa', href: '/company/edit', icon: BuildingOfficeIcon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR, ROLES.COMPANY_ADMIN] },
  { name: 'Configuración', href: '/company/settings', icon: Cog6ToothIcon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR, ROLES.COMPANY_ADMIN] },
];

const allManagementNav = [
  { name: 'Sucursales', href: '/branchoffice/manager', icon: BuildingStorefrontIcon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR, ROLES.COMPANY_ADMIN] },
  { name: 'Gestión de Usuarios', href: '/user/manager', icon: UsersIcon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR, ROLES.COMPANY_ADMIN] },
  { name: 'Planes y Suscripción', href: '/subscriptions/plans', icon: CreditCardIcon, roles: [ROLES.SUPERADMIN, ROLES.ADMINISTRATOR] },
];

const allSupportNav = [
  { name: 'Manual de Usuario', href: '/manual', icon: BookOpenIcon, roles: 'all' as const },
  { name: 'Preguntas Frecuentes', href: '/faq', icon: QuestionMarkCircleIcon, roles: 'all' as const },
];

// Helper function to check if user has access to a nav item
const hasAccess = (itemRoles: string[] | 'all', userRole: string | undefined): boolean => {
  if (!userRole) return true;
  if (itemRoles === 'all') return true;
  const normalizedUserRole = normalizeRole(userRole);
  return itemRoles.includes(normalizedUserRole);
};

// Helper to generate test ID from navigation item name
const getNavTestId = (itemName: string) => {
  return testId('nav', itemName.toLowerCase().replace(/\s+/g, '-'), 'link');
};

export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();


  // Filter navigation items based on user role
  const userRole = user?.role;

  const navigation = useMemo(() =>
    allNavigation.filter(item => hasAccess(item.roles, userRole)),
    [userRole]
  );

  const companyNav = useMemo(() =>
    allCompanyNav.filter(item => hasAccess(item.roles, userRole)),
    [userRole]
  );

  const managementNav = useMemo(() =>
    allManagementNav.filter(item => hasAccess(item.roles, userRole)),
    [userRole]
  );

  const supportNav = useMemo(() =>
    allSupportNav.filter(item => hasAccess(item.roles, userRole)),
    [userRole]
  );

  const handleNavClick = (itemName: string, href: string, menuType: 'sidebar' | 'mobile') => {
    trackingHelpers.trackNavigationClick(itemName, href, menuType);
  };

  return (
    <div className="min-h-screen bg-secondary-50 dark:bg-secondary-900">
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50 lg:hidden" onClose={setSidebarOpen}>
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-secondary-900/80" />
          </Transition.Child>

          <div className="fixed inset-0 flex">
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1" data-testid={TEST_IDS.NAV.SIDEBAR_MOBILE}>
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                    <button
                      type="button"
                      className="-m-2.5 p-2.5"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon className="h-6 w-6 text-white" aria-hidden="true" />
                    </button>
                  </div>
                </Transition.Child>

                <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-white dark:bg-secondary-800 px-6 pb-4">
                  <div className="flex h-16 shrink-0 items-center justify-center">
                    <Logo height={40} />
                  </div>
                  <nav className="flex flex-1 flex-col">
                    <ul role="list" className="flex flex-1 flex-col gap-y-7">
                      <li>
                        <div className="text-xs font-semibold leading-6 text-secondary-400">Principal</div>
                        <ul role="list" className="-mx-2 mt-2 space-y-1">
                          {navigation.map((item) => (
                            <li key={item.name}>
                              <Link
                                to={item.href}
                                onClick={() => {
                                  handleNavClick(item.name, item.href, 'mobile');
                                  setSidebarOpen(false);
                                }}
                                className={cn(
                                  location.pathname.startsWith(item.href)
                                    ? 'bg-secondary-100 dark:bg-secondary-700 text-primary-600 dark:text-primary-400'
                                    : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700',
                                  'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                                )}
                              >
                                <item.icon className="h-6 w-6 shrink-0" />
                                {item.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </li>
                      {companyNav.length > 0 && (
                        <li>
                          <div className="text-xs font-semibold leading-6 text-secondary-400">Empresa</div>
                          <ul role="list" className="-mx-2 mt-2 space-y-1">
                            {companyNav.map((item) => (
                              <li key={item.name}>
                                <Link
                                  to={item.href}
                                  onClick={() => {
                                    handleNavClick(item.name, item.href, 'mobile');
                                    setSidebarOpen(false);
                                  }}
                                  className={cn(
                                    location.pathname === item.href
                                      ? 'bg-secondary-100 dark:bg-secondary-700 text-primary-600 dark:text-primary-400'
                                      : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700',
                                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                                  )}
                                >
                                  <item.icon className="h-6 w-6 shrink-0" />
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>
                      )}
                      {managementNav.length > 0 && (
                        <li>
                          <div className="text-xs font-semibold leading-6 text-secondary-400">Administración</div>
                          <ul role="list" className="-mx-2 mt-2 space-y-1">
                            {managementNav.map((item) => (
                              <li key={item.name}>
                                <Link
                                  to={item.href}
                                  onClick={() => {
                                    handleNavClick(item.name, item.href, 'mobile');
                                    setSidebarOpen(false);
                                  }}
                                  className={cn(
                                    location.pathname === item.href
                                      ? 'bg-secondary-100 dark:bg-secondary-700 text-primary-600 dark:text-primary-400'
                                      : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700',
                                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                                  )}
                                >
                                  <item.icon className="h-6 w-6 shrink-0" />
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>
                      )}
                      {supportNav.length > 0 && (
                        <li>
                          <div className="text-xs font-semibold leading-6 text-secondary-400">Soporte</div>
                          <ul role="list" className="-mx-2 mt-2 space-y-1">
                            {supportNav.map((item) => (
                              <li key={item.name}>
                                <Link
                                  to={item.href}
                                  onClick={() => {
                                    handleNavClick(item.name, item.href, 'mobile');
                                    setSidebarOpen(false);
                                  }}
                                  className={cn(
                                    location.pathname === item.href
                                      ? 'bg-secondary-100 dark:bg-secondary-700 text-primary-600 dark:text-primary-400'
                                      : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700',
                                    'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold'
                                  )}
                                >
                                  <item.icon className="h-6 w-6 shrink-0" />
                                  {item.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </li>
                      )}
                    </ul>
                  </nav>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className={cn(
        "hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col transition-all duration-300",
        sidebarCollapsed ? "lg:w-20" : "lg:w-72"
      )} data-testid={TEST_IDS.NAV.SIDEBAR_DESKTOP}>
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 px-4 pb-4">
          <div className="flex h-16 shrink-0 items-center justify-between">
            {!sidebarCollapsed && (
              <Logo height={32} />
            )}
            <button
              type="button"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 rounded-md text-secondary-500 hover:text-secondary-700 dark:hover:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700"
              title={sidebarCollapsed ? "Expandir menú" : "Colapsar menú"}
              data-testid={TEST_IDS.NAV.SIDEBAR_TOGGLE}
            >
              {sidebarCollapsed ? (
                <ChevronRightIcon className="h-6 w-6" />
              ) : (
                <ChevronLeftIcon className="h-6 w-6" />
              )}
            </button>
          </div>
          <nav className="flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                {!sidebarCollapsed && (
                  <div className="text-xs font-semibold leading-6 text-secondary-400 mb-2">Principal</div>
                )}
                <ul role="list" className="-mx-2 space-y-1">
                  {navigation.map((item) => (
                    <li key={item.name}>
                      <Link
                        to={item.href}
                        onClick={() => handleNavClick(item.name, item.href, 'sidebar')}
                        className={cn(
                          location.pathname.startsWith(item.href)
                            ? 'bg-secondary-100 dark:bg-secondary-700 text-primary-600 dark:text-primary-400'
                            : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700',
                          'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold',
                          sidebarCollapsed && 'justify-center'
                        )}
                        title={sidebarCollapsed ? item.name : ''}
                        data-testid={getNavTestId(item.name)}
                      >
                        <item.icon className="h-6 w-6 shrink-0" />
                        {!sidebarCollapsed && item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
              {companyNav.length > 0 && (
                <li>
                  {!sidebarCollapsed && (
                    <div className="text-xs font-semibold leading-6 text-secondary-400 mb-2">Empresa</div>
                  )}
                  <ul role="list" className="-mx-2 space-y-1">
                    {companyNav.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          onClick={() => handleNavClick(item.name, item.href, 'sidebar')}
                          className={cn(
                            location.pathname === item.href
                              ? 'bg-secondary-100 dark:bg-secondary-700 text-primary-600 dark:text-primary-400'
                              : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700',
                            'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold',
                            sidebarCollapsed && 'justify-center'
                          )}
                          title={sidebarCollapsed ? item.name : ''}
                        >
                          <item.icon className="h-6 w-6 shrink-0" />
                          {!sidebarCollapsed && item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              )}
              {managementNav.length > 0 && (
                <li>
                  {!sidebarCollapsed && (
                    <div className="text-xs font-semibold leading-6 text-secondary-400 mb-2">Administración</div>
                  )}
                  <ul role="list" className="-mx-2 space-y-1">
                    {managementNav.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          onClick={() => handleNavClick(item.name, item.href, 'sidebar')}
                          className={cn(
                            location.pathname === item.href
                              ? 'bg-secondary-100 dark:bg-secondary-700 text-primary-600 dark:text-primary-400'
                              : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700',
                            'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold',
                            sidebarCollapsed && 'justify-center'
                          )}
                          title={sidebarCollapsed ? item.name : ''}
                        >
                          <item.icon className="h-6 w-6 shrink-0" />
                          {!sidebarCollapsed && item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              )}
              {supportNav.length > 0 && (
                <li>
                  {!sidebarCollapsed && (
                    <div className="text-xs font-semibold leading-6 text-secondary-400 mb-2">Soporte</div>
                  )}
                  <ul role="list" className="-mx-2 space-y-1">
                    {supportNav.map((item) => (
                      <li key={item.name}>
                        <Link
                          to={item.href}
                          onClick={() => handleNavClick(item.name, item.href, 'sidebar')}
                          className={cn(
                            location.pathname === item.href
                              ? 'bg-secondary-100 dark:bg-secondary-700 text-primary-600 dark:text-primary-400'
                              : 'text-secondary-700 dark:text-secondary-300 hover:bg-secondary-100 dark:hover:bg-secondary-700',
                            'group flex gap-x-3 rounded-md p-2 text-sm leading-6 font-semibold',
                            sidebarCollapsed && 'justify-center'
                          )}
                          title={sidebarCollapsed ? item.name : ''}
                        >
                          <item.icon className="h-6 w-6 shrink-0" />
                          {!sidebarCollapsed && item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </li>
              )}
            </ul>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className={cn(
        "transition-all duration-300",
        sidebarCollapsed ? "lg:pl-20" : "lg:pl-72"
      )}>
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800 px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-secondary-700 dark:text-secondary-300 lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid={TEST_IDS.NAV.MOBILE_MENU_BUTTON}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="relative flex flex-1"></div>

            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Company/Tenant Switcher */}
              <TenantSwitcher />

              {/* User menu */}
              <Menu as="div" className="relative" data-testid={TEST_IDS.NAV.USER_MENU}>
                <Menu.Button className="flex items-center gap-x-2 text-sm">
                  <div className="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-semibold">
                    {user?.firstName?.[0]}{user?.lastName?.[0]}
                  </div>
                  <span className="hidden lg:block text-secondary-700 dark:text-secondary-300">
                    {user?.firstName} {user?.lastName}
                  </span>
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right rounded-lg bg-white dark:bg-secondary-800 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => logout()}
                            className={cn(
                              active && 'bg-secondary-100 dark:bg-secondary-700',
                              'group flex w-full items-center px-4 py-2 text-sm text-secondary-700 dark:text-secondary-300'
                            )}
                            data-testid={TEST_IDS.NAV.LOGOUT_BUTTON}
                          >
                            <ArrowRightOnRectangleIcon className="mr-3 h-5 w-5" />
                            Cerrar Sesión
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-10">
          <div className="px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
